// Agents on the web from the writer's side (PROTOCOL.md §8): the switch that makes a project server-readable (its key
// escrowed to the relay), the grants agents hold, the audit log of their calls (details opened here with the project
// key), and the consent page's calls.
import { fromB64u, toB64u } from '../shared/b64.ts';
import { fingerprint } from '../shared/crypto.ts';
import { escrowKid, openAgentCall, wrapForEscrow, type AgentCallDetails } from '../shared/escrow.ts';
import type { Api } from './api.ts';

export interface Gates {
  comment: boolean;
  organise: boolean;
  add: boolean;
  trash: boolean;
}

export const DEFAULT_GATES: Gates = { comment: true, organise: false, add: false, trash: false };

export interface AgentGrant {
  id: string;
  clientId: string;
  clientName: string;
  redirectHost: string;
  projects: string[];
  gates: Gates;
  createdAt: number;
  lastUsedAt: number | null;
  calls: { ok: number; refused: number; error: number; lastAt: number | null };
}

export interface AgentsInfo {
  device: { id: string; name: string; createdAt: number; lastSeenAt: number } | null;
  grants: AgentGrant[];
}

export interface AgentCall {
  /** This row's id (the sealed details' associated data names it). */
  id: string;
  /** The call's: shared by its rows when it touched several projects. */
  callId: string;
  at: number;
  grantId: string;
  client: string;
  tool: string;
  outcome: 'ok' | 'refused' | 'error';
  ms: number;
  sealed: string | null;
}

/** Turns Agents on the web on: the project key wrapped to the relay's escrow key (checked against its id). */
export async function turnOnAgents(api: Api, accountId: string, projectId: string, projectKey: Uint8Array): Promise<void> {
  const { kid, pub } = await api.get<{ kid: string; pub: string }>('/api/escrow-key');
  const point = fromB64u(pub);
  if ((await escrowKid(point)) !== kid) throw new Error('The relay’s escrow key didn’t match its id.');
  const escrow = await wrapForEscrow(point, accountId, projectId, projectKey);
  await api.call('PUT', `/api/projects/${encodeURIComponent(projectId)}/escrow`, { kid, escrow: toB64u(escrow) });
}

export async function turnOffAgents(api: Api, projectId: string): Promise<void> {
  await api.call('DELETE', `/api/projects/${encodeURIComponent(projectId)}/escrow`);
}

/** Whether each of the account's projects is server-readable (`GET /api/projects`' `agents`). */
export async function agentsProjects(api: Api): Promise<Map<string, boolean>> {
  const { projects } = await api.get<{ projects: { id: string; agents?: boolean }[] }>('/api/projects');
  return new Map(projects.map((p) => [p.id, !!p.agents]));
}

export const agentsInfo = (api: Api) => api.get<AgentsInfo>('/api/agents');

export async function revokeGrant(api: Api, grantId: string): Promise<void> {
  await api.call('DELETE', `/api/agents/grants/${encodeURIComponent(grantId)}`);
}

export async function agentCalls(api: Api, projectId: string, o: { before?: number; limit?: number } = {}): Promise<AgentCall[]> {
  const q = new URLSearchParams({ project: projectId });
  if (o.before !== undefined) q.set('before', String(o.before));
  if (o.limit !== undefined) q.set('limit', String(o.limit));
  return (await api.get<{ calls: AgentCall[] }>(`/api/agents/calls?${q}`)).calls;
}

/** A call's arguments and result, opened with the project key (null when the call kept none). */
export async function openCall(projectKey: Uint8Array, accountId: string, projectId: string, call: AgentCall): Promise<AgentCallDetails | null> {
  if (!call.sealed) return null;
  return openAgentCall(projectKey, accountId, projectId, call.id, fromB64u(call.sealed));
}

/** The project key's fingerprint, as the relay checks the escrow against it. */
export const keyFingerprint = (key: Uint8Array) => fingerprint(key);

// The consent page ---------------------------------------------------------------------------------------------------

export interface ConsentRequest {
  handle: string;
  client: { name: string; domain?: string; redirectHost: string; loopback: boolean };
  scopes: string[];
  /** `others`: other people write in it (co-writers, commenters, beta readers), so their words reach the agent. */
  projects: { id: string; title: string; others?: boolean }[];
}

export const beginConsent = (api: Api, query: string) => api.call<ConsentRequest>('POST', '/api/oauth/begin', { query });

export const approveConsent = (api: Api, handle: string, projects: string[], gates: Gates) =>
  api.call<{ redirectTo: string }>('POST', '/api/oauth/approve', { handle, projects, gates });

export const denyConsent = (api: Api, handle: string) => api.call<{ redirectTo: string }>('POST', '/api/oauth/deny', { handle });
